<template>
  <div>
    <div class="d-flex mb-3 align-items-center justify-content-between">
      <div class="d-flex gap-4 align-items-center">
        <div class="me-3">
          <label class="d-inline-flex align-items-center mb-0">
            Show
            <b-form-select class="form-select form-select-sm mx-2" v-model="perPage" size="sm"
              :options="[10, 25, 50, 100]"></b-form-select>
            entries
          </label>
        </div>
        <div class=" text-md-end mt-3 mt-md-0">
          <b-form-input v-model="filter" type="search" placeholder="Search applications..."
            class="form-control form-control-sm d-inline-block w-auto"></b-form-input>
        </div>
      </div>

      <div class="col-auto text-end">
        <b-button size="sm" variant="primary" v-b-modal.new-application-modal v-if="List > 0 || IsBusy">
          <i class="mdi mdi-plus"></i> New Loan Application
        </b-button>
      </div>
    </div>

    <div class="table-responsive">
      <b-table :items="Items" :fields="Fields" responsive="sm" :per-page="perPage" :current-page="currentPage"
        :filter="filter" :busy="IsBusy" show-empty class="align-middle table-nowrap table-hover">

        <template #table-busy>
          <div class="text-center text-primary my-5">
            <b-spinner class="align-middle" type="grow"></b-spinner>
            <div class="mt-2 fw-medium">Loading Applications...</div>
          </div>
        </template>

        <template #empty>
          <div class="text-center py-5">
            <div class="mb-3">
              <i class="mdi mdi-text-box-remove-outline text-muted" style="font-size: 64px;"></i>
            </div>
            <h5 class="text-dark">No Pending Applications</h5>
            <p class="text-muted mb-4">There are currently no loans awaiting approval.</p>
            <b-button variant="primary" v-b-modal.new-application-modal class="px-4">
              <i class="mdi mdi-plus me-1"></i> New Loan Application
            </b-button>
          </div>
        </template>

        <template #cell(index)="data">{{ data.index + 1 }}</template>

        <template #cell(loan_number)="data">
          <span class="fw-medium">{{ data.item.loan_number }}</span>
          <small class="d-block text-primary fw-bold">{{ data.item.member_name }} - {{ data.item.member_id }}</small>
        </template>

        <template #cell(application_date)="data">
          <span>{{ formatDate(data.item.application_date) }}</span>
          <small class="d-block fw-bold">Period: <span class="text-primary">{{ data.item.period }} Months</span></small>
        </template>

        <template #cell(loan_product)="data">
          <span class="fw-medium">{{ data.item.loan_product }}</span>
          <small class="d-block fw-bold">Rate: <span class="text-primary">{{ data.item.rate }}% {{ data.item.rate_type
            == 'MONTHLY' ? 'Monthly' : 'Annually' }}</span></small>
        </template>

        <template #cell(amount)="data">
          <span class="fw-bold">{{ userData.currency }} {{ commas(data.item.amount) }}</span>

          <!-- Dynamic Payment Structure & Installment -->
          <small class="d-block text-muted mt-1" v-if="data.item.payment_structure">
            <i class="mdi mdi-calendar-sync"></i> Paid {{ data.item.payment_structure.charAt(0) +
              data.item.payment_structure.slice(1).toLowerCase() }}
            <div v-if="data.item.installment_amount" class="text-dark mt-1">
              {{ userData.currency }}
              <span v-if="data.item.installment_amount.min !== undefined">
                {{ commas(data.item.installment_amount.min.toFixed(0)) }} - {{
                  commas(data.item.installment_amount.max.toFixed(0)) }}
              </span>
              <span v-else>
                {{ commas(data.item.installment_amount.toFixed(0)) }}
              </span>
            </div>
          </small>
        </template>

        <template #cell(interest_display)="data">
          <span class="text-danger fw-medium" v-if="data.item.best_interest !== undefined">
            {{ userData.currency }} {{ commas(data.item.best_interest.toFixed(0)) }} - {{
              commas(data.item.worst_interest.toFixed(0)) }}
          </span>
          <span class="text-danger fw-medium" v-else>
            {{ userData.currency }} {{ commas(data.item.interest.toFixed(0)) }}
          </span>
        </template>

        <template #cell(status)="data">
          <b-badge variant="warning">{{ data.item.status }}</b-badge>
        </template>

        <template #cell(actions)="data">
          <span class="d-flex gap-1">
            <b-button title="Approve Application" size="sm" variant="info" @click="promptApprove(data.item)">
              <i class="mdi mdi-check-bold"></i>
            </b-button>
            <b-button title="Edit Application" size="sm" variant="primary">
              <i class="mdi mdi-pencil"></i>
            </b-button>
            <b-button title="Delete Application" size="sm" variant="danger" @click="promptDelete(data.item)">
              <i class="mdi mdi-trash-can"></i>
            </b-button>
          </span>
        </template>
      </b-table>
    </div>

    <div class="d-flex justify-content-end mt-3" v-if="List > 0">
      <b-pagination v-model="currentPage" :total-rows="List" :per-page="perPage"></b-pagination>
    </div>

    <b-modal id="new-application-modal" title="New Loan Application" size="xl" hide-footer hide-header centered
      @hidden="resetModal">
      <div class="modal-header border-0 pb-0 mb-4">
        <div class="d-flex align-items-center">
          <div class="bg-primary bg-opacity-10 p-2 me-3"
            style="border-radius: 20rem; width: 3rem; height: 3rem; display: flex; align-items: center; justify-content: center;">
            <i class="mdi mdi-bank text-primary" style="font-size: 2.0rem;"></i>
          </div>
          <div>
            <h5 class="modal-title fw-bold mb-0">New Loan Application</h5>
            <small class="text-muted">Fill in the details to create a new loan</small>
          </div>
        </div>
      </div>
      <NewApplication ref="newAppForm" @close="$bvModal.hide('new-application-modal')" @success="handleSuccess" />
    </b-modal>

    <!-- Comprehensive Approve Modal -->
    <b-modal id="approve-modal" size="xl" hide-footer hide-header>
      <div class="my-3">
        <div class="text-center mb-4">
          <div class="display-4 text-info mb-2"><i class="mdi mdi-check-circle-outline"></i></div>
          <h4 class="text-info mb-1">Review & Approve Application</h4>
          <p class="fs-6 text-muted mb-0">Please verify the application information before approving.</p>
        </div>

        <div v-if="isLoadingDetails" class="text-center py-5">
          <b-spinner variant="primary"></b-spinner>
          <p class="mt-2 text-muted">Retrieving comprehensive loan details...</p>
        </div>

        <div v-else-if="detailsError" class="text-center py-5">
          <i class="mdi mdi-alert-circle-outline display-4 text-danger mb-2"></i>
          <h6 class="text-danger">Failed to load application details.</h6>
          <b-button size="sm" variant="outline-danger" class="mt-2"
            @click="fetchLoanDetails(selectedApplication.id)">Retry</b-button>
        </div>

        <!-- Success Details View -->
        <div v-else-if="detailedApplication" class="px-2 px-md-4">
          <div class="row">
            <div class="col-lg-5 pe-lg-4 mb-4 mb-lg-0 border-end-lg">
              <h6 class="text-uppercase text-muted fw-bold small mb-2"><i class="mdi mdi-account-circle me-1"></i>
                Applicant Details</h6>
              <div class="d-flex align-items-center mb-2 p-2 bg-light rounded border shadow-sm">
                <div class="avatar-md me-3 flex-shrink-0">
                  <span class="avatar-title rounded-circle bg-soft-primary text-white fs-2">{{
                    selectedApplication.member_name.charAt(0) }}</span>
                </div>
                <div>
                  <h5 class="mb-1 text-dark fw-bold">{{ selectedApplication.member_name }}</h5>
                  <p class="mb-0 text-muted small">Member ID: <strong>{{ selectedApplication.member_id }}</strong>
                    &bull; Applied: <strong>{{ formatDate(detailedApplication.loan.application_date) }}</strong></p>
                </div>
              </div>

              <div class="card border-primary border-opacity-50 shadow-sm mb-0">
                <div class="card-body bg-soft-primary bg-opacity-10 rounded p-4">
                  <p class="text-uppercase fw-bold text-primary small mb-1"><i class="mdi mdi-cash-multiple me-1"></i>
                    Requested Loan Amount</p>
                  <h1 class="text-primary fw-bold mb-2 fs-1">{{ userData.currency }} {{
                    commas(detailedApplication.loan.amount) }}</h1>
                  <h5 class="text-dark fw-bold mb-4">{{ detailedApplication.product.name }}</h5>

                  <div class="d-flex justify-content-between border-bottom border-primary border-opacity-25 pb-2 mb-3">
                    <span class="text-muted">Interest Profile:</span>
                    <span class="fw-bold text-dark">{{ detailedApplication.loan.rate }}% {{
                      detailedApplication.loan.rate_type }} ({{ detailedApplication.loan.interest_type.replace('_', ' ')
                      }})</span>
                  </div>

                  <div class="d-flex justify-content-between border-bottom border-primary border-opacity-25 pb-2 mb-3">
                    <span class="text-muted">Loan Period:</span>
                    <span class="fw-bold text-dark">{{ detailedApplication.loan.period }} Months</span>
                  </div>

                  <div class="d-flex justify-content-between align-items-center pt-1 mb-2">
                    <span class="text-muted fw-bold">Expected Interest:</span>
                    <span class="text-danger fw-bold fs-5" v-if="detailedApplication.interest.min !== undefined">
                      {{ userData.currency }} {{ commas(detailedApplication.interest.min.toFixed(0)) }} <span
                        class="text-muted fs-6 fw-normal mx-1">to</span> {{
                          commas(detailedApplication.interest.max.toFixed(0)) }}
                    </span>
                    <span class="text-danger fw-bold fs-5" v-else>
                      {{ userData.currency }} {{ commas(detailedApplication.interest.toFixed(0)) }}
                    </span>
                  </div>

                  <!-- Installment Information if passed by backend -->
                  <div class="alert alert-secondary border-secondary mt-3 mb-0 py-2 px-3"
                    v-if="detailedApplication.loan.payment_structure && detailedApplication.installment_amount">
                    <div class="d-flex justify-content-between align-items-center">
                      <span class="small fw-medium"><i class="mdi mdi-calendar-refresh me-1"></i> {{
                        detailedApplication.loan.payment_structure.charAt(0) +
                        detailedApplication.loan.payment_structure.slice(1).toLowerCase() }} Est. Installment:</span>
                      <span class="fw-bold text-dark" v-if="detailedApplication.installment_amount.min !== undefined">
                        {{ userData.currency }} {{ commas(detailedApplication.installment_amount.min.toFixed(0)) }} - {{
                          commas(detailedApplication.installment_amount.max.toFixed(0)) }}
                      </span>
                      <span class="fw-bold text-dark" v-else>
                        {{ userData.currency }} {{ commas(detailedApplication.installment_amount.toFixed(0)) }}
                      </span>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            <!-- RIGHT COLUMN: Security & Guarantors -->
            <div class="col-lg-4 ps-lg-4">
              <div class="mb-4">
                <h6 class="text-primary border-bottom pb-2 mb-3"><i class="mdi mdi-account-group me-1"></i> Attached
                  Guarantors</h6>
                <div
                  v-if="detailedApplication.guarantors.length > 0 || detailedApplication.guest_guarantors.length > 0">
                  <div v-for="g in detailedApplication.guarantors" :key="'g' + g.id"
                    class="d-flex align-items-center p-2 mb-1 border rounded bg-white shadow-sm">
                    <div class="avatar-sm me-3 flex-shrink-0"><span
                        class="avatar-title rounded-circle bg-soft-success text-white fs-5"><i
                          class="mdi mdi-account-check"></i></span></div>
                    <div class="flex-grow-1">
                      <h6 class="mb-0 text-dark font-size-15">{{ g.name || g.member_id }}</h6><small
                        class="text-muted">Registered Member</small>
                    </div>
                  </div>
                  <div v-for="gg in detailedApplication.guest_guarantors" :key="'gg' + gg.id"
                    class="d-flex align-items-center p-3 mb-2 border rounded bg-white shadow-sm">
                    <div class="avatar-sm me-3 flex-shrink-0"><span
                        class="avatar-title rounded-circle bg-soft-secondary text-white fs-5"><i
                          class="mdi mdi-account-outline"></i></span></div>
                    <div class="flex-grow-1">
                      <h6 class="mb-0 text-dark font-size-15">{{ gg.name }}</h6><small class="text-muted"><i
                          class="mdi mdi-phone me-1"></i>{{ gg.contact }}</small>
                    </div>
                    <b-badge variant="soft-secondary" class="ms-auto">Guest</b-badge>
                  </div>
                </div>
                <div class="text-muted p-4 text-center border border-dashed rounded bg-light mb-4" v-else>
                  <i class="mdi mdi-account-off-outline fs-3 d-block mb-1"></i><small>No guarantors attached to this
                    application.</small>
                </div>
              </div>
            </div>

            <div class="col-3">
              <div class="mb-2">
                <h6 class="text-primary border-bottom pb-2 mb-3"><i class="mdi mdi-safe me-1"></i> Pledged Collateral
                </h6>
                <div v-if="detailedApplication.collaterals.length > 0">
                  <div v-for="c in detailedApplication.collaterals" :key="'c' + c.id"
                    class="p-3 mb-3 border rounded bg-white shadow-sm">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <h6 class="mb-0 text-dark font-size-15 fw-bold">{{ c.name }}</h6>
                      <span v-if="c.value" class="badge bg-soft-success text-success fs-6">{{ userData.currency }} {{
                        commas(c.value) }}</span>
                    </div>
                    <p class="small text-muted mb-2" v-if="c.description">{{ c.description }}</p>
                    <div v-if="c.media_url" class="mt-3 pt-2 border-top border-light">
                      <a :href="c.media_url" target="_blank"
                        class="btn btn-sm btn-soft-info d-inline-flex align-items-center"><i
                          class="mdi mdi-file-document-outline fs-5 me-1"></i> View Attached Document</a>
                    </div>
                  </div>
                </div>
                <div class="text-muted p-4 text-center border border-dashed rounded bg-light mb-2" v-else>
                  <i class="mdi mdi-shield-off-outline fs-3 d-block mb-1"></i><small>No collateral security
                    pledged.</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="d-flex justify-content-center mt-3 mb-2 pt-3 border-top bg-light rounded-bottom px-3 py-3 mx-n3 mb-n3">
        <b-button variant="light" @click="$bvModal.hide('approve-modal')" class="me-3 px-4 shadow-sm"
          :disabled="isActionLoading">Cancel</b-button>
        <b-button variant="info" @click="executeApprove" class="px-5 fw-bold shadow-sm"
          :disabled="isActionLoading || isLoadingDetails || detailsError">
          <span v-if="isActionLoading"><b-spinner small class="me-1"></b-spinner> Approving...</span>
          <span v-else><i class="mdi mdi-check-bold me-1"></i> Verify & Approve Loan</span>
        </b-button>
      </div>
    </b-modal>

    <b-modal id="delete-modal" hide-footer centered hide-header>
      <div class="text-center my-4">
        <div class="display-4 text-danger mb-2"><i class="mdi mdi-trash-can-outline"></i></div>
        <h4 class="text-danger mb-3">Delete Application?</h4>
        <p class="fs-6 text-muted mb-4">
          Are you sure you want to delete application <strong>{{ selectedApplication ? selectedApplication.loan_number :
            ''
          }}</strong>? This application will be securely archived.
        </p>
      </div>
      <div class="d-flex justify-content-center mt-4">
        <b-button variant="light" @click="$bvModal.hide('delete-modal')" class="me-3 px-4"
          :disabled="isActionLoading">Cancel</b-button>
        <b-button variant="danger" @click="executeDelete" class="px-4" :disabled="isActionLoading">
          <span v-if="isActionLoading"><b-spinner small class="me-1"></b-spinner> Deleting...</span>
          <span v-else><i class="mdi mdi-trash-can me-1"></i> Delete</span>
        </b-button>
      </div>
    </b-modal>

  </div>
</template>

<script>
import axios from "axios";
import NewApplication from "./newapplication.vue";
import { loanapplicationComputed, loanapplicationMethods } from "@/state/helpers";

export default {
  components: {
    NewApplication
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      userData: JSON.parse(localStorage.getItem("user")),
      perPage: 10,
      currentPage: 1,
      filter: null,
      selectedApplication: null,
      isActionLoading: false,

      isLoadingDetails: false,
      detailsError: false,
      detailedApplication: null,

      fields: [
        { key: 'index', label: '#', class: 'align-middle', visible: true },
        { key: 'loan_number', label: 'Loan Ref', sortable: true, class: 'align-middle', visible: true },
        { key: 'loan_product', label: 'Product Details', sortable: false, class: 'align-middle', visible: true },
        { key: 'application_date', label: 'Applied On', sortable: true, class: 'align-middle', visible: true },
        { key: 'amount', label: 'Principal', sortable: true, class: 'align-middle', visible: true },
        { key: 'interest_display', label: 'Expected Interest', class: 'align-middle', visible: true },
        { key: 'actions', label: 'Actions', class: 'align-middle', visible: true },
        { key: 'id', label: 'ID', class: 'align-middle', visible: false },
        { key: 'loan_product_id', label: 'Loan Product ID', class: 'align-middle', visible: false },
        { key: 'member_id', label: 'Member ID', class: 'align-middle', visible: false }
      ],
    };
  },
  computed: {
    ...loanapplicationComputed,
    List() { return this.loanapplications ? this.loanapplications.length : 0; },
    Fields() { return this.fields.filter(field => field.visible); },
    Items() { return this.loanapplications ? this.loanapplications.map((d) => ({ ...d })) : []; },
    IsBusy() { return this.isLoading; },
  },
  methods: {
    ...loanapplicationMethods,
    commas(x) { return x ? x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : 0; },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date)) return dateString;
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
    },

    async handleSuccess() {
      this.$bvModal.hide('new-application-modal');
      await this.fetchLoanApplications();
    },

    resetModal() {
      if (this.$refs.newAppForm) {
        this.$refs.newAppForm.resetForm();
      }
    },

    promptApprove(item) {
      this.selectedApplication = item;
      this.detailedApplication = null;
      this.$bvModal.show('approve-modal');
      this.fetchLoanDetails(item.id);
    },

    async fetchLoanDetails(id) {
      this.isLoadingDetails = true;
      this.detailsError = false;
      try {
        const res = await axios.get(`/loans/${id}/details`);
        this.detailedApplication = res.data;
      } catch (error) {
        this.detailsError = true;
        this.$toast.error('Failed to load application details.');
      } finally {
        this.isLoadingDetails = false;
      }
    },

    promptDelete(item) {
      this.selectedApplication = item;
      this.$bvModal.show('delete-modal');
    },

    async executeApprove() {
      if (!this.selectedApplication) return;
      this.isActionLoading = true;

      try {
        const today = new Date().toISOString().split('T')[0];
        await axios.post(`/loans/approve/${this.selectedApplication.id}`, {
          approved_date: today
        });

        this.$toast.success('Loan application approved successfully!');
        this.$bvModal.hide('approve-modal');
        await this.fetchLoanApplications();
      } catch (error) {
        this.$toast.error('Failed to approve application.');
      } finally {
        this.isActionLoading = false;
        this.selectedApplication = null;
      }
    },

    async executeDelete() {
      if (!this.selectedApplication) return;
      this.isActionLoading = true;

      try {
        await axios.delete(`/loans/${this.selectedApplication.id}`);

        this.$toast.success('Loan application deleted successfully!');
        this.$bvModal.hide('delete-modal');
        await this.fetchLoanApplications();
      } catch (error) {
        this.$toast.error('Failed to delete application.');
      } finally {
        this.isActionLoading = false;
        this.selectedApplication = null;
      }
    }
  }
};
</script>

<style scoped>
@media (min-width: 992px) {
  .border-end-lg {
    border-right: 1px solid #dee2e6;
  }
}

.border-dashed {
  border-style: dashed !important;
  border-width: 2px !important;
  border-color: #dee2e6 !important;
}
</style>